import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { isFPIPolicy, isMobile } from '../../services/utils';
import {
  CHANGE_COVERAGE,
  CHANGE_COVERAGE_SUBMIT_SUCCESS,
  CHANGE_COVERAGE_WEB,
} from '../navigation/routes.constants';
import {
  clearPolicyNotifications,
  getChangeCoveragePreview,
  getPrivateToken,
  petSelected,
  policySelected,
  restartRate,
  setSelectedPetId,
  setSelectedPetIndex,
} from '../../actions';
import { MobileHeader } from '../navigation/MobileHeader';
import {
  useAlphabeticalSortedPetsWithPolicy,
  useDirectCancel,
  usePostMessage,
  useQuery,
} from '../../hooks';
import { usePetPolicies } from '../../hooks/usePetPolicies';
import { SubmitSuccess } from './changeCoverageSubmitted/SubmitSuccess';
import {
  ChangeCoverageScreenPreview,
  ChangeCoverageScreenOptions,
} from '../insurance/changeCoverage';
import { CancelRenewalSent }
from '../insurance/changeCoverage/changeCoverageOptions/CancelRenewalSent';
import { CancelRenewalSuccess }
from '../insurance/changeCoverage/changeCoverageOptions/CancelRenewalSuccess';
import { POST_MESSAGE_TYPES } from '../../constants';

import './ChangeCoverageScreen.css';

const ChangeCoverageScreen = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();
  const postMessage = usePostMessage();
  const showDirectCancel = useDirectCancel();
  const publicTokenParam = query.get('publicToken');
  const [policyNumber, setPolicyNumber] = useState(match.params.policyNumber
    || '');
  const [publicToken, setPublicToken] = useState(publicTokenParam || '');
  const store = useSelector(({
    policies,
    session,
    settings,
  }) => ({ policies, session, settings }), shallowEqual);
  const {
    policies: {
      allPets,
      changeCoverage: {
        cancelMultibrandPolicy,
        cancelRenewalRequestSent,
        success,
        preview,
      },
      loading: policiesLoading,
    },
    settings: { featureFlags },
  } = store;
  const sortedPetsWithPolicy =
    useAlphabeticalSortedPetsWithPolicy();
  const { hasPets } = usePetPolicies();

  function handlePolicySelect(index) {
    const { policies } = store;

    dispatch(clearPolicyNotifications());

    if (!policies.allPolicies.length) {
      return;
    }

    dispatch(policySelected(index));
  }

  function handlePetSelect(index) {
    const petId = allPets[index].PetId;
    const indexBillingPayment = sortedPetsWithPolicy
      .findIndex((pet) => pet.PetId === petId);
    const indexNotFound = -1;
    const firstIndex = 0;
    dispatch(petSelected(indexBillingPayment === indexNotFound
      ? firstIndex
      : indexBillingPayment));
    dispatch(setSelectedPetId(petId));
    dispatch(clearPolicyNotifications());
    dispatch(setSelectedPetIndex(index));
  }

  useEffect(() => {
    if (policiesLoading) {
      return;
    }

    if (hasPets && store.session.privateToken) {
      const petIndexToSelect = sortedPetsWithPolicy
        .findIndex((pet) => pet.policies
          .find((policy, index) => {
            const policyFound = policy.Number === policyNumber;
            if (policyFound) {
              handlePolicySelect(index);
            }
            return policyFound;
          }));

      if (petIndexToSelect >= 0) {
        handlePetSelect(petIndexToSelect);
      }
    }
  }, [
    dispatch,
    hasPets,
    policyNumber,
    sortedPetsWithPolicy,
    store.session.privateToken,
  ]);

  useEffect(() => () => {
    dispatch(restartRate());
  }, []);

  useEffect(() => {
    setPolicyNumber(match.params.policyNumber || '');
    if (!publicToken) {
      setPublicToken(publicTokenParam);
    }
  }, [match.params, publicTokenParam]);

  useEffect(() => {
    if (!isMobile()) {
      history.replace(`${CHANGE_COVERAGE_WEB}/${policyNumber}`);
    } else if (!publicToken) {
      history.replace('/');
    }
  }, [publicToken]);

  useEffect(() => {
    if (!store.session.privateToken && publicToken && !featureFlags.loading) {
      dispatch(getPrivateToken({ publicToken }));
    }
  }, [
    dispatch,
    featureFlags.loading,
    publicToken,
    store.session.privateToken,
  ]);

  useEffect(() => {
    if (store.session.privateToken && isFPIPolicy(policyNumber)) {
      dispatch(getChangeCoveragePreview({ policyNumber }));
    }
  }, [dispatch, policyNumber, store.session.privateToken]);

  useEffect(() => {
    if (success) {
      history.push(CHANGE_COVERAGE_SUBMIT_SUCCESS);
    }
  }, [history, success]);

  function handleChangeCoverageButton() {
    history.push(`${match.url}/options`);
  }

  function handleKeepCoverageButton() {
    postMessage(POST_MESSAGE_TYPES.CLOSE_PAGE);
  }

  function handleRateError() {
    history.goBack();
  }

  function shouldPromptWarning() {
    return false;
  }

  function renderContent() {
    if (!store.session.privateToken) {
      return null;
    }

    if (showDirectCancel
      && !cancelMultibrandPolicy.loading
      && cancelMultibrandPolicy.success
    ) {
      return (
        <CancelRenewalSuccess
          onReturnClick={handleKeepCoverageButton}
          renewalDate={preview?.NewPremiumEffectiveDate}
        />
      );
    }

    if (!showDirectCancel && cancelRenewalRequestSent) {
      return <CancelRenewalSent onReturnClick={handleKeepCoverageButton} />;
    }

    return (
      <>
        <Switch>
          <Route path={`${match.path}/options`}>
            <ChangeCoverageScreenOptions
              onRateCreationError={handleRateError}
              policyNumber={policyNumber}
              validatePromptWarning={shouldPromptWarning}
            />
          </Route>

          <Route path={CHANGE_COVERAGE_SUBMIT_SUCCESS}>
            <SubmitSuccess onClose={handleKeepCoverageButton} />
          </Route>

          <Route path={CHANGE_COVERAGE}>
            <ChangeCoverageScreenPreview
              onChangeCoverageClick={handleChangeCoverageButton}
              onKeepClick={handleKeepCoverageButton}
              policyNumber={policyNumber}
            />
          </Route>

        </Switch>
      </>
    );
  }

  return (
    <div
      className={`Change-coverage-main-container${cancelRenewalRequestSent
        || cancelMultibrandPolicy.success
        ? '-request-sent'
        : ''
        }`}
    >
      <MobileHeader />

      {renderContent()}
    </div>
  );
};

export { ChangeCoverageScreen };
